import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getImageSource } from 'utils/helpers/imageSource';
import { PopupWraper } from '../popup';
import './styles.scss';

export type ImageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** URL, relative/imported asset, data URL, raw Base64, Blob or File. */
  src?: string | Blob | null;
  /** Explicit raw Base64, including formats without a recognized signature. */
  base64?: string | null;
  mimeType?: string;
  title?: string;
  alt?: string;
};

const PreviewImage = ({ src, alt }: { src: string; alt: string }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  return (
    <>
      {loading && <p role='status'>Đang tải ảnh...</p>}
      {error && <p role='alert'>Không thể tải hình ảnh.</p>}
      <img
        src={src}
        alt={alt}
        hidden={error}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
      />
    </>
  );
};

const OpenImageModal = ({
  src,
  base64,
  mimeType,
  onClose,
  title = 'Xem hình ảnh',
  alt = title,
}: ImageModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [blobSource, setBlobSource] = useState<{
    blob: Blob;
    url: string;
  } | null>(null);

  useEffect(() => {
    if (!(src instanceof Blob)) return;
    const url = URL.createObjectURL(src);
    setBlobSource({ blob: src, url });
    return () => URL.revokeObjectURL(url);
  }, [src]);

  useLayoutEffect(() => {
    const modal = modalRef.current;
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      const popups = document.querySelectorAll('.PopupWraper');
      if (popups[popups.length - 1] !== modal) {
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      } else if (event.key === 'Tab') {
        // The shared close button is the only control in this image preview.
        event.preventDefault();
        modal?.querySelector<HTMLButtonElement>('.btnClose')?.focus();
      }
    };
    modal?.querySelector<HTMLButtonElement>('.btnClose')?.focus();
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      if (
        previouslyFocused instanceof HTMLElement &&
        previouslyFocused.isConnected
      ) {
        previouslyFocused.focus();
      }
    };
  }, [onClose]);

  const imageSource =
    src instanceof Blob
      ? blobSource?.blob === src
        ? blobSource.url
        : ''
      : getImageSource(src || base64, mimeType, !src && !!base64);

  return (
    <PopupWraper
      ref={modalRef}
      className='ImageModal'
      center
      title={title}
      onClose={onClose}
      style={{ maxWidth: 720 }}
    >
      <div className='ImageModal__body'>
        {imageSource ? (
          <PreviewImage key={imageSource} src={imageSource} alt={alt} />
        ) : (
          <p role='status'>Chưa có hình ảnh để hiển thị.</p>
        )}
      </div>
    </PopupWraper>
  );
};

/** Image content inside the system popup, with keyboard focus and Escape dismissal. */
export const ImageModal = (props: ImageModalProps) =>
  props.isOpen ? <OpenImageModal {...props} /> : null;
